/**
 * @author NikolaV
 */
import '.././ui-native/Dropdown.scss';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cs } from '../utils/utils-classname';
import { Manager, Popper, Reference } from 'react-popper';
import { OutsideClick } from '@shared/ui-react/react-outside-click-handler';
import './Dropdown.scss';
import { Tooltip } from '@shared/ui-react/Tooltip';


const tooltipModifiers = [{
    name: 'offset',
    enabled: true,
    options: {
        offset: [0, 13],
    },
}];

const instances = new Set<Dropdown>();

export class Dropdown extends React.Component<{
    className?: string,
    bodyClassName?: string,
    styledBody?: boolean,
    disabled?: boolean,
    closeOnBodyClick?: boolean,
    onClose?: () => any,
    onOpen?: () => any,
    onToggle?: (boolean) => any,
    isOpen?: boolean,
    lazyRender?: boolean,
    buttonElem?: string,
    tooltip?: string,
    title?: string,
    triggerStyle?: React.CSSProperties,
    buttonStyle?: React.CSSProperties,
    bodyStyle?: React.CSSProperties,
    position?: 'left' | 'right',
    getBody?: Function,
    stopPropagation?: boolean,
    children?: React.ReactNode
}> {
    static defaultProps = {
        className: 'ik-button-secondary',
        closeOnBodyClick: false,
        lazyRender: true,
        buttonElem: 'button',
        position: 'left',
        styledBody: true,
        triggerStyle: {},
        bodyStyle: {}
    };

    state = {
        isOpen: false,
    };

    private bodyRef: HTMLDivElement;
    private buttonRef: HTMLElement;

    componentDidMount() {
        instances.add(this);
    }

    componentWillUnmount() {
        instances.delete(this);
    }

    popperProps = {
        placement: (this.props.position === 'left' ? 'bottom-start' : 'bottom-end') as any,
        positionFixed: true,
        modifiers: {
            preventOverflow: {
                boundariesElement: 'viewport' as any,
                escapeWithReference: false
            },
        },
    };

    render() {
        const { lazyRender, triggerStyle, tooltip } = this.props;
        const isOpen = this.isOpen();

        const bodyClass = cs({
            'dropdown-react-wrapper': this.props.styledBody,
            'dropdown-off': !isOpen,
        });

        const buttonClass = cs(this.props.className, 'dropdown-toggle-btn', { 'dropdown-open': isOpen });
        const ButtonElem = this.props.buttonElem as any;

        const Btn = <ButtonElem ref={el => this.buttonRef = el} type='button' className={buttonClass} title={this.props.title ?? null} style={this.props.buttonStyle} disabled={this.props.disabled} onClick={this.toggle}>
            {React.Children.toArray(this.props.children)[0]}
        </ButtonElem>;

        return (
            <Manager>
                <Reference>
                    {({ ref }) => (
                        <span ref={ref} style={{ display: 'inline-flex', ...triggerStyle }} className='dropdown-react-trigger'>
                            {tooltip ? <Tooltip tooltip={tooltip} placement='top' hideArrow={false} modifiers={tooltipModifiers} portalContainer={document.body}>{Btn}</Tooltip> : Btn}
                        </span>
                    )}
                </Reference>

                {ReactDOM.createPortal(
                    <Popper {...this.popperProps} innerRef={el => this.bodyRef = el}>
                        {({ placement, ref, style, scheduleUpdate }) => (
                            !isOpen && lazyRender ? null : (
                                <div ref={ref} className={this.props.bodyClassName} style={{
                                    display: isOpen ? 'block' : 'none',
                                    zIndex: 10000, // <dialog> zindex + 1
                                    ...style
                                }} data-placement={placement}>
                                    <OutsideClick onOutsideClick={e => this.onOutsideClick(e, scheduleUpdate)}>
                                        <div className={bodyClass} onClick={this.onBodyClick} style={this.props.bodyStyle}>
                                            {this.props?.getBody ? this.props?.getBody() : React.Children.toArray(this.props.children)[1]}
                                        </div>
                                    </OutsideClick>
                                </div>
                            )
                        )}
                    </Popper>,
                    document.body
                )}
            </Manager>
        );
    }

    toggle = (e) => {
        if (this.props.stopPropagation) {
            e.stopPropagation();
        }
        this.isOpen() ? this.close() : this.open();
    };

    open = () => {
        if (typeof this.props.onToggle == 'function') {
            this.props.onToggle(true);
        }
        else {
            this.setState({
                isOpen: true,
            });

            if (typeof this.props.onOpen === 'function') {
                this.props.onOpen();
            }

            Array.from(instances).forEach(i => {
                if (i !== this && i.isOpen() && !i.bodyRef?.contains(this.buttonRef) /* not my parent*/) {
                    console.log('close', i.buttonRef);
                    i.close(true);
                }
            })
        }
    };

    close = (silent = false) => {
        if (!silent && typeof this.props.onClose === 'function') {
            this.props.onClose();
        }

        if (typeof this.props.onToggle == 'function') {
            this.props.onToggle(false);
        }
        else {
            this.setState({
                isOpen: false,
            });
        }
    };

    private isControlled() {
        return (typeof this.props.isOpen !== 'undefined');
    }

    private isOpen() {
        return this.isControlled() ? this.props.isOpen : this.state.isOpen;
    }

    private readonly onBodyClick = (e) => {
        if (this.props.stopPropagation) {
            e.stopPropagation();
        }
        if (this.props.closeOnBodyClick) this.close();
    };

    private readonly onOutsideClick = (e, scheduleUpdate) => {
        scheduleUpdate();
        if (this.isOpen() && !this.buttonRef?.contains(e.target)) {
            const clickedInsideDropdown = !!e.target.closest('.dropdown-react-wrapper') ||
                !!e.target.closest('.react-datepicker-popper') || /* we can have datepickers inside dropdowns which are appended to <body> */
                !!e.target.closest('.p-component'); /* allow clicking inside primeng components */

            // dont close if clicked inside any dropdown, to allow nested dropdowns
            if (!clickedInsideDropdown) {
                this.close(false);
            }
        }
    }
}


export class DropdownSelect extends React.Component<{
    items: { label: string, value: any }[],
    value: any,
    showRadio?: boolean,
    showCaret?: boolean,
    onChange: (value) => void
}> {
    render() {
        const foundItem = this.props.items.filter(i => i.value == this.props.value)[0];
        return (
            <Dropdown closeOnBodyClick={true} buttonElem='div' className='ik-dropdown-toggle'>
                <span>{foundItem ? foundItem.label : ' / '} {this.props.showCaret ? <i className='iconf-caret'></i> : null}</span>
                <ul className='clickable'>
                    {this.props.items.map(i => (
                        <li onClick={() => this.handleSelect(i.value)} key={i.value}>
                            {this.props.showRadio ? <input type='radio' checked={i.value == this.props.value} readOnly={true} /> : null} {i.label}
                        </li>
                    ))}
                </ul>
            </Dropdown>
        );
    }

    handleSelect = value => {
        this.props.onChange(value);
    };
}
